import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { RequestModeProvider } from "./utils/useMode";
import { SongCartProvider } from "./context/SongCartContext";
import { AuthProvider } from "./context/AuthContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";


import AdminRoute from "./Components/AdminRoute";

// Admin
import Login from "./Components/AdminSide/Login";
import AdminLoginPage from "./Components/AdminSide/LoginPage";
import AdminConfig from "./Components/AdminSide/AdminConfig";
import Unauthorized from "./Components/AdminSide/Unauthorized";
import AdminRequestCompact from "./Components/AdminSide/AdminRequestCompact";
import AdminHomePage from "./Components/AdminSide/AdminHomePage";
import AdminPlaylist from "./Components/AdminSide/AdminPlaylist";
import AdminRequests from "./Components/AdminSide/AdminRequests";
import SongqRequests from "./Components/AdminSide/SongqRequests";
import ImportedSongs from "./Components/AdminSide/ImportedSongs/ImportedSongs";
import RequestedSongs from "./Components/AdminSide/RequestedSongs";
import SongsMaster from "./Components/AdminSide/songs/SongsMaster";
import DuplicateSongs from "./Components/AdminSide/songs/DuplicateSongs";
import LookupTabs from "./Components/AdminSide/lookups/LookupsMaster";
import PlaylistMaster from "./Components/AdminSide/playlists/PlaylistMaster";
import TrackListPage from "./Components/AdminSide/TrackListPage";
import Quiz from "./Components/AdminSide/quiz/QuizDashboard";
import QuizLookupMaster from "./Components/AdminSide/quizLookup/QuizLookupMaster";
import QuizImportMaster from "./Components/AdminSide/quizImport/QuizImportMaster";
import HigherOrLowerDashboard from "./Components/AdminSide/games/HigherOrLowerDashboard";
import SpinningWheelDashboard from "./Components/AdminSide/games/SpinningWheelDashboard";
import BuzzerDashboard from "./Components/AdminSide/games/BuzzerDashboard";
import BeerillionerDashboard from "./Components/AdminSide/games/BeerillionerDashboard";
import SoundSystemDashboard from "./Components/AdminSide/sounds/SoundSystemDashboard";
import SoundPalette from "./Components/AdminSide/sounds/SoundPalette";
import PrizePalette from "./Components/AdminSide/prizes/PrizePalette";
import ActiveUsersWidget from "./Components/AdminSide/ActiveUsersWidget";
import AiQuestionDashboard from "./Components/AdminSide/ai/AiQuestionDashboard";
import ShowTimeManagement from "./Components/AdminSide/showTime/ShowTimeManagement";
import GigModePage from "./Components/AdminSide/gigMode/GigModePage";
import SpectatorControl from "./Components/AdminSide/SpectatorControl";
import AdminTicketDashboard from "./Components/AdminSide/tickets/AdminTicketDashboard";
import AdminCheckIn from "./Components/AdminSide/tickets/AdminCheckIn";

// Spectator
import SpectatorPage from "./Components/UserSide/spectator/SpectatorPage";

// Tickets
import TicketEvents from "./Components/UserSide/tickets/TicketEvents";
import TicketPurchase from "./Components/UserSide/tickets/TicketPurchase";
import TicketSuccess from "./Components/UserSide/tickets/TicketSuccess";

// User 
import Home from "./Components/UserSide/pages/Home";
import ShowdownForm from "./Components/UserSide/pages/ShowdownForm";
import PlaylistPage from "./Components/UserSide/PlaylistPage";
import ArtistSongsPage from "./Components/UserSide/ArtistSongsPage";
import QuizMaster from "./Components/UserSide/quiz/QuizMaster";
import QuizHistory from "./Components/UserSide/quiz/QuizHistory";
import MindYourMusic from "./Components/UserSide/pages/MindYourMusic";
import ShowTimeCodeEntry from "./Components/UserSide/showTime/ShowTimeCodeEntry";
import ShowTimeLobby from "./Components/UserSide/showTime/ShowTimeLobby";
import ShowTimePayment from "./Components/UserSide/showTime/ShowTimePayment";
import HigherOrLower from "./Components/UserSide/pages/HigherOrLower";
import SpinningWheel from "./Components/UserSide/pages/SpinningWheel";
import BuzzerPage from "./Components/UserSide/pages/BuzzerPage";
import BeerillionerScreen from "./Components/UserSide/beerillioner/BeerillionerScreen";
import EmailVerificationPage from "./Components/UserSide/auth/EmailVerificationPage";
import PasswordResetPage from "./Components/UserSide/auth/PasswordResetPage";
import AuthCallback from "./Components/UserSide/auth/AuthCallback";
import UserLoginPage from "./Components/UserSide/auth/LoginPage";
import UserSignupPage from "./Components/UserSide/auth/SignupPage";
import GuestSetupPage from "./Components/UserSide/auth/GuestSetupPage";
import AdminAnalytics from "./Components/AdminSide/Analytics/AdminAnalytics";
import ActiveUserTracker from "./Components/Common/ActiveUserTracker";
import BuzzerForceOverlay from "./Components/Common/BuzzerForceOverlay";
import QuizForceOverlay from "./Components/Common/QuizForceOverlay";
import MymForceNavigator from "./Components/Common/MymForceNavigator";
import BrowserBackGuard from "./Components/Common/BrowserBackGuard";
import LoginRequiredGuard from "./Components/Common/LoginRequiredGuard";
import ShowTimeMymGuard from "./Components/Common/ShowTimeMymGuard";
import ShowTimeSessionTracker from "./Components/Common/ShowTimeSessionTracker";
import NotFound from "./Components/Common/NotFound";
import SoundPaletteListener from "./Components/Common/SoundPaletteListener";
import GameSoundChannelMuteSync from "./Components/Common/GameSoundChannelMuteSync";
import PrizeRevealOverlay from "./Components/Common/PrizeRevealOverlay";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#121210",
      paper: "#1A1A1A",
    },
    text: {
      primary: "#fff",
      secondary: "rgba(255,255,255,0.7)",
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#1A1A1A",
        },
      },
    },
  },
});


const queryClient = new QueryClient();

const App = () => {
  return (
    <AuthProvider>
      <SongCartProvider>
        <SnackbarProvider>
          <ThemeProvider theme={darkTheme}>
            <QueryClientProvider client={queryClient}>
              <CssBaseline />
              <Router>
                <RequestModeProvider>
                  <ShowTimeSessionTracker />
                  <ActiveUserTracker />
                  <BuzzerForceOverlay />
                  <QuizForceOverlay />
                  <MymForceNavigator />
                  <BrowserBackGuard />
                  <SoundPaletteListener />
                  <GameSoundChannelMuteSync />
                  <PrizeRevealOverlay />
                  <LoginRequiredGuard>
                    <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/playlist/:id" element={<PlaylistPage />} />
                    <Route path="/artist/:artistID" element={<ArtistSongsPage />} />
                    <Route path="/AdminLogin" element={<AdminLoginPage />} />
                    <Route path="/showdown" element={<ShowdownForm />} />
                    <Route path="/quiz" element={<QuizMaster />} />
                    <Route path="/quiz/history" element={<QuizHistory />} />
                    <Route path="/mindyourmusic" element={<ShowTimeMymGuard><MindYourMusic /></ShowTimeMymGuard>} />
                    <Route path="/showtime" element={<ShowTimeCodeEntry />} />
                    <Route path="/showtime/lobby/:showTimeId" element={<ShowTimeLobby />} />
                    <Route path="/showtime/payment/:showTimeId" element={<ShowTimePayment />} />
                    <Route path="/higherorlower" element={<HigherOrLower />} />
                    <Route path="/spinningwheel" element={<SpinningWheel />} />
                    <Route path="/buzzer" element={<BuzzerPage />} />
                    <Route path="/beerillioner" element={<BeerillionerScreen />} />
                    <Route path="/spectate" element={<SpectatorPage />} />
                    <Route path="/tickets" element={<TicketEvents />} />
                    <Route path="/tickets/success" element={<TicketSuccess />} />
                    <Route path="/tickets/:eventId" element={<TicketPurchase />} />
                    <Route path="/verify-email" element={<EmailVerificationPage />} />
                    <Route path="/reset-password" element={<PasswordResetPage />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/login" element={<UserLoginPage />} />
                    <Route path="/signup" element={<UserSignupPage />} />
                    <Route path="/guest-setup" element={<GuestSetupPage />} />
                    {/* Admin Routes (no authentication required) */}
                    <Route element={<AdminRoute />}>
                      <Route path="/sp-login" element={<Login />} />
                      <Route path="/AdminHomePage" element={<AdminHomePage />} />
                      <Route path="/AdminConfig" element={<AdminConfig />} />
                      <Route path="/AdminRequestCompact" element={<AdminRequestCompact />} />
                      <Route path="/AdminPlaylist" element={<AdminPlaylist />} />
                      <Route path="/AdminRequests" element={<AdminRequests />} />
                      <Route path="/SongqRequests" element={<SongqRequests />} />
                      <Route path="/analytics" element={<AdminAnalytics />} />
                      {/* Songs master (support both old and lowercase URLs) */}
                      <Route path="/AllSongScreen" element={<SongsMaster />} />
                      <Route path="/allsongscreen" element={<SongsMaster />} />
                      <Route path="/DuplicateSongs" element={<DuplicateSongs />} />
                      {/* Lookup tabs (support both old and lowercase URLs) */}
                      <Route path="/LookUpTabs" element={<LookupTabs />} />
                      <Route path="/lookuptabs" element={<LookupTabs />} />

                      {/* Playlist master (support both old and lowercase URLs) */}
                      <Route path="/AllPlaylistScreen" element={<PlaylistMaster />} />
                      <Route path="/allplaylistscreen" element={<PlaylistMaster />} />
                      <Route
                        path="/playlists/:playlistId"
                        element={<TrackListPage />}
                      />
                      <Route path="/ImportedSongs" element={<ImportedSongs />} />
                      <Route path="/RequestedSongs" element={<RequestedSongs />} />
                      <Route path="/AdminQuiz" element={<Quiz />} />
                      <Route path="/AdminLookup" element={<QuizLookupMaster />} />
                      <Route path="/AdminQuizImport" element={<QuizImportMaster />} />
                      <Route path="/AdminHigherOrLower" element={<HigherOrLowerDashboard />} />
                      <Route path="/AdminSpinningWheel" element={<SpinningWheelDashboard />} />
                      <Route
                        path="/AdminBuzzer"
                        element={
                          <>
                            <BuzzerDashboard />
                            <ActiveUsersWidget />
                          </>
                        }
                      />
                      <Route path="/AdminAiQGen" element={<AiQuestionDashboard />} />
                      <Route path="/AdminShowTime" element={<ShowTimeManagement />} />
                      <Route path="/AdminBeerillioner" element={<BeerillionerDashboard />} />
                      <Route path="/AdminSounds" element={<SoundSystemDashboard />} />
                      <Route path="/AdminSoundPalette" element={<SoundPalette />} />
                      <Route path="/AdminPrizePalette" element={<PrizePalette />} />
                      <Route path="/AdminGigMode" element={<GigModePage />} />
                      <Route path="/AdminSpectator" element={<SpectatorControl />} />
                      <Route path="/AdminTicketDashboard" element={<AdminTicketDashboard />} />
                      <Route path="/AdminCheckIn" element={<AdminCheckIn />} />
                      <Route path="/AdminCoupons" element={<Navigate to="/AdminTicketDashboard?tab=coupons" replace />} />
                    </Route>

                    {/* Fallback */}
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route path="/404notfound" element={<NotFound />} />
                    <Route path="*" element={<Navigate to="/404notfound" replace />} />
                    </Routes>
                  </LoginRequiredGuard>
                </RequestModeProvider>
              </Router>
            </QueryClientProvider>
          </ThemeProvider>
        </SnackbarProvider>
      </SongCartProvider>
    </AuthProvider>
  );
};

export default App;
